import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import {
  DISABLE_MFA_AUTHENTICATOR_TOTP,
  DISABLE_MFA_EMAIL_TOTP,
  DISABLE_MFA_SMS_TOTP,
  REQUEST_MFA_EMAIL_TOTP,
  REQUEST_MFA_SMS_TOTP,
  MfaMethodType,
  MFA_METHOD_CONFIGS,
  RequestMfaEmailTotpResponse,
  RequestMfaSmsTotpResponse,
} from '../graphql/mfa';
import { createOtpSchema, backupCodeSchema } from '../schemas/mfaValidation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ButtonComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DialogComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPGroupComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPSlotComponentType = React.ComponentType<any>;

export interface MfaDisableDialogProps {
  methodToDisable: MfaMethodType;
  onSuccess: () => void;
  onCancel: () => void;
  DialogComponent: DialogComponentType;
  ButtonComponent: ButtonComponentType;
  InputOTPComponent: InputOTPComponentType;
  InputOTPGroupComponent: InputOTPGroupComponentType;
  InputOTPSlotComponent: InputOTPSlotComponentType;
}

type AuthCodeType = 'AUTHENTICATOR_TOTP' | 'EMAIL_TOTP' | 'SMS_TOTP' | 'BACKUP_TOKEN';

const METHOD_TO_AUTH_TYPE: Record<MfaMethodType, AuthCodeType> = {
  authenticator: 'AUTHENTICATOR_TOTP',
  email: 'EMAIL_TOTP',
  sms: 'SMS_TOTP',
};

const BACKUP_CODE_LENGTH = 8;

export function MfaDisableDialog({
  methodToDisable,
  onSuccess,
  onCancel,
  DialogComponent,
  ButtonComponent,
  InputOTPComponent,
  InputOTPGroupComponent,
  InputOTPSlotComponent,
}: MfaDisableDialogProps) {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const hasSentInitialCode = useRef(false);

  const config = MFA_METHOD_CONFIGS[methodToDisable];
  const authCodeType: AuthCodeType = useBackupCode
    ? 'BACKUP_TOKEN'
    : METHOD_TO_AUTH_TYPE[methodToDisable];
  const codeLength = useBackupCode ? BACKUP_CODE_LENGTH : config.codeLength;

  const sendCode = useCallback(async () => {
    // Authenticator doesn't need to send a code
    if (methodToDisable === 'authenticator') {
      setCodeSent(true);
      return;
    }

    setSendingCode(true);
    setError(null);
    try {
      if (methodToDisable === 'email') {
        await client.mutate({
          mutation: REQUEST_MFA_EMAIL_TOTP,
        });
      } else if (methodToDisable === 'sms') {
        await client.mutate({
          mutation: REQUEST_MFA_SMS_TOTP,
        });
      }
      setCodeSent(true);
    } catch {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  }, [client, methodToDisable]);

  // Send code on mount for email/SMS methods
  useEffect(() => {
    if (hasSentInitialCode.current) return;
    hasSentInitialCode.current = true;
    sendCode();
  }, [sendCode]);

  const getDisableMutation = useCallback(() => {
    switch (methodToDisable) {
      case 'authenticator':
        return DISABLE_MFA_AUTHENTICATOR_TOTP;
      case 'email':
        return DISABLE_MFA_EMAIL_TOTP;
      case 'sms':
        return DISABLE_MFA_SMS_TOTP;
    }
  }, [methodToDisable]);

  const handleDisable = useCallback(async () => {
    const schema = useBackupCode ? backupCodeSchema : createOtpSchema(codeLength);
    const result = schema.safeParse({ code });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await client.mutate({
        mutation: getDisableMutation(),
        variables: {
          input: {
            auth_code: code,
            auth_code_type: authCodeType,
          },
        },
      });
      onSuccess();
    } catch (err) {
      setError('Invalid code. Please check and try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [client, code, codeLength, authCodeType, useBackupCode, getDisableMutation, onSuccess]);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        onCancel();
      }
    },
    [onCancel]
  );

  const handleResend = useCallback(() => {
    setCode('');
    setError(null);
    sendCode();
  }, [sendCode]);

  const handleToggleBackupCode = useCallback(() => {
    setUseBackupCode((prev) => !prev);
    setCode('');
    setError(null);
  }, []);

  const getInstructionText = () => {
    if (useBackupCode) {
      return 'Enter one of your backup codes';
    }
    switch (methodToDisable) {
      case 'authenticator':
        return 'Enter the 6-digit code from your authenticator app';
      case 'email':
        return 'Enter the 8-digit code sent to your email';
      case 'sms':
        return 'Enter the 6-digit code sent to your phone';
    }
  };

  const getMethodCodeLabel = () => {
    switch (methodToDisable) {
      case 'authenticator':
        return 'authenticator';
      case 'email':
        return 'email';
      case 'sms':
        return 'SMS';
    }
  };

  return (
    <DialogComponent
      isOpen={true}
      setIsOpen={handleClose}
      title={`Disable ${config.label}`}
      centered
    >
      <div className="flex flex-col gap-6 items-center mx-auto w-full max-w-md">
        {error && (
          <div className="w-full p-3 text-sm text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30">
              <span className="text-3xl text-red-600 material-symbols-outlined dark:text-red-400">
                {config.icon}
              </span>
            </div>
          </div>
          <p className="text-grey-600 dark:text-grey-300">
            To disable {config.label.toLowerCase()}, enter a verification code.
          </p>
        </div>

        {sendingCode ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-grey-500 dark:text-grey-400">Sending verification code...</p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <p className="mb-4 text-sm text-grey-500 dark:text-grey-400">
                {getInstructionText()}
              </p>
            </div>

            <InputOTPComponent
              value={code}
              onChange={(value: string) => setCode(useBackupCode ? value.toUpperCase() : value)}
              maxLength={codeLength}
              disabled={loading}
              pattern={useBackupCode ? '^[a-zA-Z0-9]+$' : undefined}
              inputMode={useBackupCode ? 'text' : 'numeric'}
            >
              <InputOTPGroupComponent>
                {Array.from({ length: codeLength }).map((_, i) => (
                  <InputOTPSlotComponent key={i} index={i} />
                ))}
              </InputOTPGroupComponent>
            </InputOTPComponent>

            {codeSent && methodToDisable !== 'authenticator' && !useBackupCode && (
              <button
                type="button"
                onClick={handleResend}
                disabled={loading || sendingCode}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
              >
                Resend Code
              </button>
            )}

            <button
              type="button"
              onClick={handleToggleBackupCode}
              disabled={loading || sendingCode}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
            >
              {useBackupCode
                ? `Use my ${getMethodCodeLabel()} code instead`
                : 'Lost access? Use a backup code instead'}
            </button>
          </>
        )}

        <div className="flex flex-col gap-3 w-full">
          <ButtonComponent
            type="button"
            variant="danger"
            onClick={handleDisable}
            disabled={code.length !== codeLength || loading || sendingCode}
            loading={loading}
          >
            Disable {config.label}
          </ButtonComponent>
          <ButtonComponent
            type="button"
            variant="tertiary"
            onClick={onCancel}
            disabled={loading || sendingCode}
          >
            Cancel
          </ButtonComponent>
        </div>
      </div>
    </DialogComponent>
  );
}
